import "@/App.css";
import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { getAccessStatus } from "./lib/api";
import { Loader2 } from "lucide-react";
import { useAppUpdate } from "./hooks/useAppUpdate";
import { usePageTracking } from "./hooks/usePageTracking";
import { captureUTMParams } from "./utils/utmTracking";
import UpdatePrompt from "./components/UpdatePrompt";

// Capture UTM parameters on initial load
captureUTMParams();

// Critical pages - load immediately
import AuthPage from "./pages/AuthPage";
import PlayerDashboard from "./pages/PlayerDashboard";
import CoachDashboard from "./pages/CoachDashboard";

// Lazy-loaded pages - load on demand for better performance
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const SkillPage = lazy(() => import("./pages/SkillPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ContentManager = lazy(() => import("./pages/ContentManager"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const RankingsPage = lazy(() => import("./pages/RankingsPage"));
const PlayerRankingsPage = lazy(() => import("./pages/PlayerRankingsPage"));
const ShotTracker = lazy(() => import("./pages/ShotTracker"));
const QuickShotLogger = lazy(() => import("./pages/QuickShotLogger"));
const GameTracker = lazy(() => import("./pages/GameTracker"));
const QAPage = lazy(() => import("./pages/QAPage"));
const CommunityFeed = lazy(() => import("./pages/CommunityFeed"));
const WorkoutPlanner = lazy(() => import("./pages/WorkoutPlanner"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const DesignPreview = lazy(() => import("./pages/DesignPreview"));
const SimpleHome = lazy(() => import("./pages/marketing/simplified/SimpleHome"));
const SimpleProgramsPage = lazy(() => import("./pages/marketing/simplified/SimpleProgramsPage"));
const SimpleSkoolPage = lazy(() => import("./pages/marketing/simplified/SimpleSkoolPage"));
const SimpleAboutPage = lazy(() => import("./pages/marketing/simplified/SimpleAboutPage"));
const SimpleHallOfFamePage = lazy(() => import("./pages/marketing/simplified/SimpleHallOfFamePage"));
const RoleWorksheetPage = lazy(() => import("./pages/marketing/simplified/RoleWorksheetPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const CoachAnalytics = lazy(() => import("./pages/CoachAnalytics"));
const CoachShotLogger = lazy(() => import("./pages/CoachShotLogger"));
const CoachGameLogger = lazy(() => import("./pages/CoachGameLogger"));
const SiteContentManager = lazy(() => import("./pages/SiteContentManager"));

// Calendar/Scheduling — legacy admin replaced by Skills Lab
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const PlayerProfilePage = lazy(() => import("./pages/PlayerProfilePage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const ShotHeatmapPage = lazy(() => import("./pages/ShotHeatmapPage"));
const IQLabLanding = lazy(() => import("./pages/IQLabLanding"));
const IQLabPricing = lazy(() => import("./pages/IQLabPricing"));
const IQLabThankYou = lazy(() => import("./pages/IQLabThankYou"));
const IQLabAssessment = lazy(() => import("./pages/IQLabAssessment"));
const DigitalLabPage = lazy(() => import("./pages/DigitalLabPage"));
const RealSkillAppPage = lazy(() => import("./pages/RealSkillAppPage"));
const RealSkillThankYou = lazy(() => import("./pages/RealSkillThankYou"));
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));
const AdminAnalyticsDashboard = lazy(() => import("./pages/AdminAnalyticsDashboard"));
const AdminPlayersPage = lazy(() => import("./pages/AdminPlayersPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminTrainingContentPage = lazy(() => import("./pages/AdminTrainingContentPage"));
const HallOfFameAdminPage = lazy(() => import("./pages/HallOfFameAdminPage"));
const SignupsAdminPage = lazy(() => import("./pages/SignupsAdminPage"));
const WorksheetsAdminPage = lazy(() => import("./pages/WorksheetsAdminPage"));
const WorksheetPrintPage = lazy(() => import("./pages/WorksheetPrintPage"));
const StripeAdminPage = lazy(() => import("./pages/StripeAdminPage"));
const CourtIQAdminPage = lazy(() => import("./pages/CourtIQAdminPage"));
const CMSAdminPage = lazy(() => import("./pages/CMSAdminPage"));
const GMBAdminPage = lazy(() => import("./pages/GMBAdminPage"));
const LiveChatAdminPage = lazy(() => import("./pages/LiveChatAdminPage"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const TrainingContentPage = lazy(() => import("./pages/TrainingContentPage"));
const OnboardingFlow = lazy(() => import("./pages/OnboardingFlow"));

// Marketing Site Pages - lazy load since not critical path
const MarketingHome = lazy(() => import("./pages/marketing/MarketingHome"));
const AboutPage = lazy(() => import("./pages/marketing/AboutPage"));
const BlogPage = lazy(() => import("./pages/marketing/BlogPage"));
const ProgramsPage = lazy(() => import("./pages/marketing/ProgramsPage"));
const EvaluationPage = lazy(() => import("./pages/marketing/EvaluationPage"));
const FeaturesPage = lazy(() => import("./pages/marketing/FeaturesPage"));
const EvaluationThankYou = lazy(() => import("./pages/marketing/EvaluationThankYou"));
const HallOfFamePage = lazy(() => import("./pages/marketing/HallOfFamePage"));
const OptInPage = lazy(() => import("./pages/marketing/OptInPage"));
const FreeEvaluationPage = lazy(() => import("./pages/marketing/FreeEvaluationPage"));
const FreeEvaluationThankYouPage = lazy(() => import("./pages/marketing/FreeEvaluationThankYouPage"));
const PricingPage = lazy(() => import("./pages/marketing/PricingPage"));
const DropInsPage = lazy(() => import("./pages/marketing/DropInsPage"));
const CourtIQPage = lazy(() => import("./pages/marketing/CourtIQPage"));
const CoachTierPage = lazy(() => import("./pages/marketing/CoachTierPage"));
const CommunityPage = lazy(() => import("./pages/marketing/CommunityPage"));
const BlogPostPage = lazy(() => import("./pages/marketing/BlogPostPage"));
const ShotLabSignupPage = lazy(() => import("./pages/marketing/ShotLabSignupPage"));
const FreeWorkoutPage = lazy(() => import("./pages/marketing/FreeWorkoutPage"));
const SkillsLabPage = lazy(() => import("./pages/marketing/SkillsLabPage"));
const SkillsLabThankYouPage = lazy(() => import("./pages/marketing/SkillsLabThankYouPage"));
const CalendarPage = lazy(() => import("./pages/marketing/CalendarPage"));
const BookPage = lazy(() => import("./pages/marketing/BookPage"));
const BookSuccessPage = lazy(() => import("./pages/marketing/BookSuccessPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/marketing/PrivacyPolicyPage"));
const SkillsLabAdminPage = lazy(() => import("./pages/SkillsLabAdminPage"));
const LinkInBioPage = lazy(() => import("./pages/LinkInBioPage"));

// Coach Pages
const CoachMyPlayersPage = lazy(() => import("./pages/CoachMyPlayersPage"));
const CoachRankingsPage = lazy(() => import("./pages/CoachRankingsPage"));
const CoachTeamsPage = lazy(() => import("./pages/CoachTeamsPage"));
const TeamGameLogPage = lazy(() => import("./pages/TeamGameLogPage"));
const CoachTeamGameLogger = lazy(() => import("./pages/CoachTeamGameLogger"));
const CoachAssistMode = lazy(() => import("./pages/CoachAssistMode"));

// Parent Dashboard Pages
const ParentViewPage = lazy(() => import("./pages/ParentViewPage"));
const ParentSharePage = lazy(() => import("./pages/ParentSharePage"));
const ParentDashboardPage = lazy(() => import("./pages/ParentDashboardPage"));

// Affiliate Program
const AffiliatePage = lazy(() => import("./pages/AffiliatePage"));

// Coach Tools
const PlayerComparisonPage = lazy(() => import("./pages/PlayerComparisonPage"));

// Gamification
const BadgesPage = lazy(() => import("./pages/BadgesPage"));
const AIShotAnalysisPage = lazy(() => import("./pages/AIShotAnalysisPage"));
const ShareableStatsPage = lazy(() => import("./pages/ShareableStatsPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));
const SharePage = lazy(() => import("./pages/SharePage"));
const PublicSharePage = lazy(() => import("./pages/PublicSharePage"));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="min-h-screen bg-gray-950 flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-white" />
  </div>
);

const ProtectedRoute = ({ children, requireCoach = false, requireAccess = true }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [accessLoading, setAccessLoading] = useState(requireAccess);
  const [hasAccess, setHasAccess] = useState(!requireAccess);
  const accessCheckRef = useRef(false);
  
  useEffect(() => {
    const checkAccess = async () => {
      // Prevent multiple simultaneous checks
      if (accessCheckRef.current) return;
      accessCheckRef.current = true;
      
      if (!requireAccess || !isAuthenticated) {
        setAccessLoading(false);
        accessCheckRef.current = false;
        return;
      }
      
      try {
        const res = await getAccessStatus();
        setHasAccess(res.data.has_access);
      } catch (error) {
        // If error, allow access (fallback) - don't block user on network issues
        console.warn('Access check failed, allowing access:', error);
        setHasAccess(true);
      } finally {
        setAccessLoading(false);
        accessCheckRef.current = false;
      }
    };
    
    if (isAuthenticated && requireAccess) {
      checkAccess();
    }
  }, [isAuthenticated, requireAccess]);
  
  if (loading || accessLoading) {
    return null; // Let the HTML splash screen handle it
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  // Redirect to payment if no access
  if (requireAccess && !hasAccess) {
    return <Navigate to="/payment" replace />;
  }
  
  if (requireCoach && user?.role !== 'coach') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Wrapper to show different rankings page based on user role
const RankingsRouteWrapper = () => {
  const { user } = useAuth();
  
  // Admin sees the admin rankings management page (Girls, Group A Boys, etc.)
  if (user?.is_admin) {
    return <RankingsPage />;
  }
  
  // Coaches see their team management page
  if (user?.role === 'coach') {
    return <CoachTeamsPage />;
  }
  
  // Players see the player-focused rankings view
  return <PlayerRankingsPage />;
};

const AppRoutes = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Track page views
  usePageTracking();
  
  return (
    <Routes>
      <Route 
        path="/auth" 
        element={isAuthenticated ? <Navigate to={user?.role === 'coach' ? '/coach' : '/dashboard'} replace /> : <AuthPage />} 
      />
      <Route 
        path="/reset-password" 
        element={<ResetPasswordPage />} 
      />
      <Route 
        path="/payment" 
        element={<ProtectedRoute requireAccess={false}><PaymentPage /></ProtectedRoute>} 
      />
      <Route 
        path="/payment/success" 
        element={<ProtectedRoute requireAccess={false}><PaymentPage /></ProtectedRoute>} 
      />
      <Route 
        path="/payment/cancel" 
        element={<ProtectedRoute requireAccess={false}><PaymentPage /></ProtectedRoute>} 
      />
      <Route 
        path="/onboarding" 
        element={<ProtectedRoute requireAccess={false}><OnboardingFlow /></ProtectedRoute>} 
      />
      <Route 
        path="/dashboard" 
        element={<ProtectedRoute><PlayerDashboard /></ProtectedRoute>} 
      />
      <Route 
        path="/category/:categoryId" 
        element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} 
      />
      <Route 
        path="/skill/:skillId" 
        element={<ProtectedRoute><SkillPage /></ProtectedRoute>} 
      />
      <Route 
        path="/chat" 
        element={<ProtectedRoute><ChatPage /></ProtectedRoute>} 
      />
      <Route 
        path="/chat/:partnerId" 
        element={<ProtectedRoute><ChatPage /></ProtectedRoute>} 
      />
      <Route 
        path="/profile" 
        element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} 
      />
      <Route 
        path="/shots" 
        element={<ProtectedRoute><QuickShotLogger /></ProtectedRoute>} 
      />
      <Route 
        path="/shots/advanced" 
        element={<ProtectedRoute><ShotTracker /></ProtectedRoute>} 
      />
      <Route 
        path="/games" 
        element={<ProtectedRoute><GameTracker /></ProtectedRoute>} 
      />
      <Route 
        path="/rankings" 
        element={
          <ProtectedRoute>
            <RankingsRouteWrapper />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/qa" 
        element={<ProtectedRoute><QAPage /></ProtectedRoute>} 
      />
      <Route 
        path="/community" 
        element={<ProtectedRoute><CommunityFeed /></ProtectedRoute>} 
      />
      <Route 
        path="/workouts" 
        element={<ProtectedRoute><WorkoutPlanner /></ProtectedRoute>} 
      />
      <Route 
        path="/referrals" 
        element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} 
      />
      <Route 
        path="/player-profile" 
        element={<ProtectedRoute><PlayerProfilePage /></ProtectedRoute>} 
      />
      <Route 
        path="/player/:userId" 
        element={<PlayerProfilePage />} 
      />
      <Route 
        path="/insights" 
        element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/heatmap" 
        element={<ProtectedRoute><ShotHeatmapPage /></ProtectedRoute>} 
      />
      <Route 
        path="/badges" 
        element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} 
      />
      <Route 
        path="/ai-analysis" 
        element={<ProtectedRoute><AIShotAnalysisPage /></ProtectedRoute>} 
      />
      <Route 
        path="/share-stats" 
        element={<ProtectedRoute><ShareableStatsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach" 
        element={<ProtectedRoute requireCoach><CoachDashboard /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/content" 
        element={<ProtectedRoute requireCoach><ContentManager /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/rankings" 
        element={<ProtectedRoute requireCoach><RankingsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/admin" 
        element={<ProtectedRoute requireCoach><AdminPanel /></ProtectedRoute>} 
      />
      <Route 
        path="/subscription" 
        element={<ProtectedRoute requireAccess={false}><SubscriptionPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/analytics" 
        element={<ProtectedRoute requireCoach><CoachAnalytics /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/shots" 
        element={<ProtectedRoute requireCoach><CoachShotLogger /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/games" 
        element={<ProtectedRoute requireCoach><CoachGameLogger /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/my-players" 
        element={<ProtectedRoute requireCoach><CoachMyPlayersPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/team-game" 
        element={<ProtectedRoute requireCoach><TeamGameLogPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/compare" 
        element={<ProtectedRoute requireCoach><PlayerComparisonPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/teams/:teamId/game" 
        element={<ProtectedRoute requireCoach><CoachTeamGameLogger /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/teams/:teamId/games" 
        element={<ProtectedRoute requireCoach><CoachTeamsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/teams" 
        element={<ProtectedRoute requireCoach><CoachTeamsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/coach/assist" 
        element={<ProtectedRoute requireCoach><CoachAssistMode /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/site-content" 
        element={<ProtectedRoute requireCoach><SiteContentManager /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/analytics" 
        element={<ProtectedRoute requireCoach><AdminAnalyticsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/marketing-analytics" 
        element={<ProtectedRoute requireCoach><AdminAnalyticsDashboard /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/players" 
        element={<ProtectedRoute requireCoach><AdminPlayersPage /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/training" 
        element={<ProtectedRoute requireCoach><AdminTrainingContentPage /></ProtectedRoute>} 
      />
      
      {/* Training Content - for players and coaches */}
      <Route 
        path="/training" 
        element={<ProtectedRoute><TrainingContentPage /></ProtectedRoute>} 
      />
      
      {/* Calendar — bare calendar page (no hero/opt-in), powered by Skills Lab data */}
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/book" element={<BookPage />} />
      <Route path="/book/success" element={<BookSuccessPage />} />
      <Route path="/book/cancel" element={<Navigate to="/book" replace />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/calendar/session/:sessionId" element={<Navigate to="/calendar" replace />} />
      <Route path="/calendar/login" element={<Navigate to="/calendar" replace />} />
      <Route path="/calendar/dashboard" element={<Navigate to="/calendar" replace />} />
      <Route path="/calendar/booking/success" element={<Navigate to="/free-evaluation/thank-you" replace />} />
      <Route path="/calendar/booking/cancel" element={<Navigate to="/calendar" replace />} />

      {/* Calendar Admin — redirected to the new Skills Lab admin */}
      <Route
        path="/admin/calendar"
        element={<ProtectedRoute requireCoach><Navigate to="/admin/skills-lab" replace /></ProtectedRoute>}
      />
      
      {/* Hall of Fame Admin */}
      <Route 
        path="/admin/hall-of-fame" 
        element={<ProtectedRoute requireCoach><HallOfFameAdminPage /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/signups" 
        element={<ProtectedRoute requireCoach><SignupsAdminPage /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/worksheets" 
        element={<ProtectedRoute requireCoach><WorksheetsAdminPage /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/worksheets/:id/print" 
        element={<ProtectedRoute requireCoach><WorksheetPrintPage /></ProtectedRoute>} 
      />
      
      {/* Stripe Revenue */}
      <Route 
        path="/admin/revenue" 
        element={<ProtectedRoute requireCoach><StripeAdminPage /></ProtectedRoute>} 
      />
      
      {/* Court IQ Data */}
      <Route 
        path="/admin/court-iq" 
        element={<ProtectedRoute requireCoach><CourtIQAdminPage /></ProtectedRoute>} 
      />
      
      {/* CMS */}
      <Route 
        path="/admin/cms" 
        element={<ProtectedRoute requireCoach><CMSAdminPage /></ProtectedRoute>} 
      />
      
      {/* Google Reviews */}
      <Route 
        path="/admin/reviews" 
        element={<ProtectedRoute requireCoach><GMBAdminPage /></ProtectedRoute>} 
      />
      
      {/* Live Chat */}
      <Route 
        path="/admin/live-chat" 
        element={<ProtectedRoute requireCoach><LiveChatAdminPage /></ProtectedRoute>} 
      />
      
      {/* Parent Dashboard - Public view via share link */}
      <Route path="/parent-view/:linkCode" element={<ParentViewPage />} />
      
      {/* Parent Dashboard - Multi-child view */}
      <Route path="/parent-dashboard" element={<ParentDashboardPage />} />
      
      {/* Parent Share - Player settings to create share links */}
      <Route 
        path="/parent-share" 
        element={<ProtectedRoute><ParentSharePage /></ProtectedRoute>} 
      />
      
      {/* Affiliate Program - Public */}
      <Route path="/affiliates" element={<AffiliatePage />} />
      
      {/* Public Player Profiles - No Auth Required */}
      <Route path="/p/:slug" element={<PublicProfilePage />} />

      {/* Share Profile - Recruit-ready public profile */}
      <Route
        path="/share"
        element={<ProtectedRoute requireAccess={false}><SharePage /></ProtectedRoute>}
      />
      <Route path="/share/:vanity" element={<PublicSharePage />} />
      
      {/* Marketing Site - Public Routes (Root Domain) — SIMPLIFIED */}
      <Route path="/preview" element={<DesignPreview />} />
      <Route path="/" element={<SimpleHome />} />
      <Route path="/about" element={<SimpleAboutPage />} />
      <Route path="/programs" element={<SimpleProgramsPage />} />
      <Route path="/pricing" element={<SimpleProgramsPage />} />
      <Route path="/skool" element={<SimpleSkoolPage />} />
      <Route path="/hall-of-fame" element={<SimpleHallOfFamePage />} />
      <Route path="/role-worksheet" element={<RoleWorksheetPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/evaluation" element={<IQLabAssessment />} />
      <Route path="/evaluation/thank-you" element={<EvaluationThankYou />} />
      <Route path="/links" element={<LinkInBioPage />} />
      <Route path="/hall-of-fame-full" element={<HallOfFamePage />} />
      <Route path="/review" element={<ReviewPage />} />
      <Route path="/pricing-legacy" element={<PricingPage />} />
      <Route path="/hall-of-fame-legacy" element={<HallOfFamePage />} />
      <Route path="/optin" element={<FreeEvaluationPage />} />
      <Route path="/free-evaluation" element={<FreeEvaluationPage />} />
      <Route path="/free-evaluation/thank-you" element={<FreeEvaluationThankYouPage />} />
      <Route path="/optin-legacy" element={<OptInPage />} />
      <Route path="/drop-ins" element={<DropInsPage />} />
      <Route path="/court-iq" element={<CourtIQPage />} />
      <Route path="/coach-tier" element={<CoachTierPage />} />
      <Route path="/community-info" element={<CommunityPage />} />
      <Route path="/blog/:postId" element={<BlogPostPage />} />
      <Route path="/shot-lab/signup" element={<ShotLabSignupPage />} />
      <Route path="/free-workout" element={<FreeWorkoutPage />} />
      <Route path="/skills-lab" element={<SkillsLabPage />} />
      <Route path="/skills-lab/thank-you" element={<SkillsLabThankYouPage />} />
      <Route 
        path="/admin/skills-lab" 
        element={<ProtectedRoute requireCoach><SkillsLabAdminPage /></ProtectedRoute>} 
      />
      
      {/* IQ Lab Landing Page */}
      <Route path="/iq-lab" element={<IQLabLanding />} />
      <Route path="/iq-lab/pricing" element={<IQLabPricing />} />
      <Route path="/iq-lab/assessment" element={<IQLabAssessment />} />
      <Route path="/iq-lab/thank-you" element={<IQLabThankYou />} />
      <Route path="/lab" element={<IQLabLanding />} />
      
      {/* Digital Lab Info Page */}
      <Route path="/digital-lab" element={<DigitalLabPage />} />
      
      {/* RealSkill App Page */}
      <Route path="/realskill-app" element={<RealSkillAppPage />} />
      <Route path="/realskill" element={<RealSkillAppPage />} />
      <Route path="/realskill-thank-you" element={<RealSkillThankYou />} />
      
      {/* Legacy /site routes - redirect to root */}
      <Route path="/site" element={<Navigate to="/" replace />} />
      <Route path="/site/about" element={<Navigate to="/about" replace />} />
      <Route path="/site/programs" element={<Navigate to="/programs" replace />} />
      <Route path="/site/blog" element={<Navigate to="/blog" replace />} />
      <Route path="/site/evaluation" element={<Navigate to="/evaluation" replace />} />
    </Routes>
  );
};

// Component to handle app updates
const AppUpdateHandler = () => {
  const { updateAvailable, applyUpdate, dismissUpdate } = useAppUpdate();
  
  if (!updateAvailable) return null;
  
  return <UpdatePrompt onUpdate={applyUpdate} onDismiss={dismissUpdate} />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoadingFallback />}>
            <AppRoutes />
          </Suspense>
          <AppUpdateHandler />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
